import { useQueryClient } from "@tanstack/react-query";

import { contextEventChannel, isContextEvent } from "@recobra/core";

import { useDocumentEvents } from "../../documents";

export const useContextEventInvalidation = ({
  contextType,
  contextId,
  caseNumber,
}: {
  contextType: "case" | "client";
  contextId: string | undefined;
  /** The case's detail cache key; only that case's detail refreshes on a task change. */
  caseNumber?: number | null;
}): void => {
  const queryClient = useQueryClient();

  useDocumentEvents(
    contextId ? contextEventChannel(contextType, contextId) : undefined,
    (payload) => {
      if (!isContextEvent(payload)) return;

      if (payload.kind === "task_changed") {
        if (contextType === "case")
          void queryClient.invalidateQueries({
            predicate: ({ queryKey: [root, key, ...subresource] }) =>
              root === "cases" &&
              (key === "list" ||
                (subresource.length === 0 && (typeof key === "object" || key === caseNumber))),
          });
        void queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] === "tasks" &&
            (queryKey[1] !== "assignees" || (contextType === "case" && queryKey[2] === contextId)),
        });
        return;
      }

      const contextRoot = contextType === "case" ? "cases" : "clients";
      if (payload.kind === "documents_changed") {
        void queryClient.invalidateQueries({ queryKey: [contextRoot, contextId, "documents"] });
        void queryClient.invalidateQueries({ queryKey: [contextRoot, contextId, "requirements"] });
        return;
      }

      void queryClient.invalidateQueries({ queryKey: [contextRoot] });
    },
  );
};
